<script setup>
import {onClickOutside} from '@vueuse/core'
import Dropdown from '~/components/UI/Dropdown.vue'
import ActingAsBadge from '~/components/Common/ActingAsBadge.vue'

const {
  isPromotionSection,
  navItems,
  promotions,
  selectedPromotion,
  openPromotion,
  isNavItemActive
} = usePromotionNavigation()

const {isOpen, close} = usePromotionSubmenu()
const route = useRoute()

// Cerrar el panel al navegar
watch(() => route.path, () => {
  if (isOpen.value) {
    close()
  }
})

const panelRef = ref(null)

onClickOutside(panelRef, () => {
  if (isOpen.value) {
    close()
  }
})

const handlePromotionSelected = (promotion) => {
  openPromotion(promotion)
  close()
}

const handleNavClick = () => {
  close()
}
</script>

<template>
  <Transition name="promotion-overlay">
    <div
      v-if="isOpen && isPromotionSection"
      class="absolute inset-0 z-40 lg:hidden"
    >
        <div
          class="absolute inset-0 bg-black/20"
          @click="close"
        />

        <div
          ref="panelRef"
          class="promotion-panel absolute top-0 left-0 h-full w-64 max-w-[85vw] bg-white shadow-2xl border-r border-slate-200 flex flex-col"
        >
          <div class="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <span class="text-xs font-bold text-slate-400 uppercase tracking-wider">
              Promoción Activa
            </span>
            <button
              type="button"
              class="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              @click="close"
            >
              <Icon name="mdi:close" class="text-lg" />
            </button>
          </div>

          <div class="flex-1 overflow-y-auto p-4">
            <Dropdown
              v-model="selectedPromotion"
              :options="promotions"
              @selected="handlePromotionSelected"
              class="mb-3"
            >
              <template #selected="{ selected }">
                <h2 class="text-sm font-bold text-slate-900 truncate">
                  {{ selected.name }}
                </h2>
                <p class="text-[10px] text-slate-500 truncate">
                  {{ selected.address?.city || 'Sin ubicación' }}
                </p>
              </template>
              <template #option="{ option }">
                <h3 class="text-sm font-bold text-slate-900 truncate">
                  {{ option.name }}
                </h3>
                <p class="text-[10px] text-slate-500 truncate">
                  {{ option.address?.city || 'Sin ubicación' }}
                </p>
              </template>
            </Dropdown>

            <ActingAsBadge class="mb-4" />

            <nav class="flex flex-col gap-1">
              <template v-for="(item, index) in navItems" :key="index">
                <p
                  v-if="item.section"
                  class="text-xs font-bold text-slate-400 uppercase tracking-wider px-2 mt-3 mb-1"
                >
                  {{ item.section }}
                </p>
                <NuxtLink
                  v-else
                  :to="item.to"
                  class="flex items-center gap-3 px-2 py-2 rounded-lg transition-colors"
                  :class="isNavItemActive(item)
                    ? 'bg-slate-100 text-slate-900 font-semibold'
                    : 'text-slate-600 hover:bg-slate-50'"
                  @click="handleNavClick"
                >
                  <div class="relative flex">
                    <Icon :name="item.icon" class="text-[20px]" />
                    <span
                        v-if="item.badge"
                        class="absolute -top-0.5 -right-0.5 size-2 bg-red-500 rounded-full border-2 border-white"/>
                  </div>
                  <span class="text-sm font-medium">{{ item.label }}</span>
                </NuxtLink>
              </template>
            </nav>
          </div>
        </div>
    </div>
  </Transition>
</template>

<style scoped>
.promotion-overlay-enter-active,
.promotion-overlay-leave-active {
  transition: opacity 0.2s ease;
}

.promotion-overlay-enter-from,
.promotion-overlay-leave-to {
  opacity: 0;
}

.promotion-overlay-enter-active .promotion-panel,
.promotion-overlay-leave-active .promotion-panel {
  transition: transform 0.25s ease;
}

.promotion-overlay-enter-from .promotion-panel,
.promotion-overlay-leave-to .promotion-panel {
  transform: translateX(-100%);
}
</style>
